// ============================================================================
// CATALOG HELPERS — built on top of MMYTE Taxonomy (catalog.ts)
// Single source of truth for slug generation. Import this everywhere a slug
// is created or parsed — never hand-build a slug string inline anywhere else
// in either the Vite app or the Next.js app.
// ============================================================================

import {
  GLOBAL_MAKES,
  MMYTE_CATEGORIES,
  MMYTE_KEYWORDS,
} from './catalog'

// ----------------------------------------------------------------------------
// Generic slugify — used for every slug type below, so casing/format rules
// only live in one place.
// ----------------------------------------------------------------------------
export function slugify(str: string): string {
  return String(str || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')   // strip punctuation
    .replace(/\s+/g, '-')       // spaces -> hyphens
    .replace(/-+/g, '-')        // collapse repeats
    .replace(/^-+|-+$/g, '')    // trim edge hyphens
}

// ----------------------------------------------------------------------------
// Category / subcategory slugs
// e.g. 'Braking System' -> 'braking'
// e.g. 'Brake Pads' -> 'brake-pads'
// ----------------------------------------------------------------------------
export function getCategorySlug(categoryKey: string): string {
  const match = MMYTE_CATEGORIES.find(
    c => c.id.toLowerCase() === categoryKey.toLowerCase() ||
         c.name.toLowerCase() === categoryKey.toLowerCase() ||
         c.code.toLowerCase() === categoryKey.toLowerCase()
  )
  return match ? match.id : slugify(categoryKey)
}

export function getSubcategorySlug(subcategoryName: string): string {
  return slugify(subcategoryName)
}

// Full list of subcategories per category, derived from MMYTE_CATEGORIES
export function getSubcategoriesByCategory(): Record<string, { name: string; slug: string }[]> {
  const result: Record<string, { name: string; slug: string }[]> = {}
  for (const cat of MMYTE_CATEGORIES) {
    result[cat.id] = (cat.subcategories || []).map(name => ({
      name,
      slug: getSubcategorySlug(name),
    }))
  }
  return result
}

// ----------------------------------------------------------------------------
// Vehicle slugs — make/model/year.
// ----------------------------------------------------------------------------
export function getMakeSlug(makeName: string): string {
  const match = GLOBAL_MAKES.find(
    m => m.id.toLowerCase() === makeName.toLowerCase() ||
         m.name.toLowerCase() === makeName.toLowerCase()
  )
  return match ? match.id : slugify(makeName)
}

export function getModelSlug(modelName: string): string {
  return slugify(modelName)
}

export function getVehiclePath(opts: {
  country?: string
  make: string
  model: string
  year?: string | number
  trim?: string
}): string {
  const country = (opts.country || 'ng').toLowerCase()
  const parts = [country, 'vehicles', getMakeSlug(opts.make), getModelSlug(opts.model)]
  if (opts.year) parts.push(String(opts.year))
  if (opts.trim) parts.push(slugify(opts.trim))
  return '/' + parts.join('/')
}

// Validate a make/model pair actually exists in the canonical catalog
export function isValidMakeModel(makeName: string, modelName: string): boolean {
  const make = GLOBAL_MAKES.find(
    m => m.id.toLowerCase() === makeName.toLowerCase() ||
         m.name.toLowerCase() === makeName.toLowerCase()
  )
  if (!make) return false
  return make.models.some(m => m.toLowerCase() === modelName.toLowerCase())
}

// ----------------------------------------------------------------------------
// Product slug — the single source of truth for product URLs.
// Pattern: {make}-{model}-{year}-{part-slug}-{shortId}
// shortId = last 6 chars of internal product UUID
// ----------------------------------------------------------------------------
export function generateProductSlug(opts: {
  make?: string
  model?: string
  year?: string | number
  partName?: string
  title?: string
  id?: string
  sku?: string
}): string {
  const idStr = String(opts.id || opts.sku || 'prod')
  const shortId = idStr.replace(/[^a-zA-Z0-9]/g, '').slice(-6).toLowerCase() || 'p4d001'

  const make = opts.make ? getMakeSlug(opts.make) : ''
  const model = opts.model ? getModelSlug(opts.model) : ''
  const year = opts.year ? String(opts.year) : ''
  const part = slugify(opts.partName || opts.title || 'auto-part')

  const segments = [make, model, year, part, shortId].filter(Boolean)
  return segments.join('-')
}

export function getProductPath(opts: { country?: string; slug: string }): string {
  const country = (opts.country || 'ng').toLowerCase()
  return `/${country}/products/${opts.slug}`
}

// ----------------------------------------------------------------------------
// Part keyword lookup — match free-text input to canonical category
// ----------------------------------------------------------------------------
export function matchPartTaxonomy(freeText: string) {
  const q = String(freeText || '').toLowerCase()
  for (const item of MMYTE_KEYWORDS) {
    if (q.includes(item.term.toLowerCase())) {
      const cat = MMYTE_CATEGORIES.find(c => c.id.toLowerCase() === item.category.toLowerCase())
      return {
        category: cat ? cat.id : slugify(item.category),
        categoryName: cat ? cat.name : item.category,
        matchedKeyword: item.term,
        subcategory: item.subcategory,
      }
    }
  }
  return null
}

// ----------------------------------------------------------------------------
// Seller slug — generated once at vendor approval, stored, never re-derived.
// ----------------------------------------------------------------------------
export function generateSellerSlug(businessName: string, existingSlugs?: Set<string>): string {
  const base = slugify(businessName) || 'merchant'
  if (!existingSlugs || !existingSlugs.has(base)) return base
  let n = 2
  while (existingSlugs.has(`${base}-${n}`)) n++
  return `${base}-${n}`
}

export function getSellerPath(opts: { country?: string; slug: string }): string {
  const country = (opts.country || 'ng').toLowerCase()
  return `/${country}/sellers/${opts.slug}`
}
